// Health content database with personalized recommendations
export const healthContentDatabase = [
  // Sleep Category
  {
    id: 'sleep_1',
    title: 'Better Sleep Through Evening Routine',
    category: 'sleep',
    icon: '😴',
    readTime: '2 min',
    triggers: ['low_sleep_quality', 'difficulty_sleeping'],
    priority: 'high',
    summary: 'Simple evening habits that prepare your body for restful sleep',
    content: {
      overview: 'Your body needs consistent signals to know it\'s time to wind down. A structured evening routine helps regulate your sleep-wake cycle naturally.',
      keyPoints: [
        'Set a consistent bedtime (within 30 minutes each night)',
        'Dim lights 2 hours before sleep to boost melatonin',
        'Avoid screens 1 hour before bed (blue light disrupts sleep)',
        'Keep bedroom cool (60-67°F / 15-19°C is optimal)',
        'Try gentle stretching or reading to signal relaxation'
      ],
      whatYouCanDoToday: [
        'Choose one bedtime and stick to it for 7 days',
        'Put your phone in another room 1 hour before sleep',
        'Lower your bedroom temperature by 2-3 degrees'
      ]
    }
  },
  {
    id: 'sleep_2',
    title: 'Foods That Support Better Sleep',
    category: 'sleep',
    icon: '🌙',
    readTime: '2 min',
    triggers: ['low_sleep_quality', 'low_energy'],
    priority: 'medium',
    summary: 'Natural foods that promote melatonin production and relaxation',
    content: {
      overview: 'Certain foods contain compounds that help your body produce sleep hormones naturally, improving both sleep quality and duration.',
      keyPoints: [
        'Bananas: Rich in magnesium and tryptophan',
        'Oats: Contain melatonin and complex carbs',
        'Almonds: High in magnesium for muscle relaxation',
        'Chamomile tea: Natural calming properties',
        'Avoid caffeine after 2 PM (stays in system 6+ hours)'
      ],
      whatYouCanDoToday: [
        'Have a small banana or handful of almonds 1 hour before bed',
        'Replace your evening coffee with chamomile tea',
        'Add oats to your dinner routine'
      ]
    }
  },

  // Hydration Category
  {
    id: 'hydration_1',
    title: 'Simple Hydration Strategies That Work',
    category: 'nutrition',
    icon: '💧',
    readTime: '2 min',
    triggers: ['low_water_intake', 'low_energy', 'headaches'],
    priority: 'high',
    summary: 'Easy ways to stay hydrated without forcing yourself to drink more',
    content: {
      overview: 'Most people struggle with hydration because they rely on willpower. Smart strategies make it automatic.',
      keyPoints: [
        'Start your day with 16oz water before coffee',
        'Drink a glass before each meal (3 glasses guaranteed)',
        'Set phone reminders every 2 hours',
        'Eat water-rich foods (cucumbers, watermelon, oranges)',
        'Track using a marked water bottle'
      ],
      whatYouCanDoToday: [
        'Fill a water bottle and mark it with hourly goals',
        'Place a glass of water by your bedside tonight',
        'Set 3 phone reminders for water breaks'
      ]
    }
  },

  // Activity Category
  {
    id: 'activity_1',
    title: 'Movement for Desk Workers',
    category: 'activity',
    icon: '🚶',
    readTime: '2 min',
    triggers: ['low_activity', 'sedentary_lifestyle'],
    priority: 'high',
    summary: 'Micro-movements that combat sitting all day',
    content: {
      overview: 'Sitting for 8+ hours daily increases health risks. But you don\'t need a gym—micro-movements throughout the day are powerful.',
      keyPoints: [
        'Stand and stretch every hour (set a timer)',
        'Take phone calls while walking',
        'Do desk exercises: shoulder rolls, neck stretches',
        'Walk during lunch break (even 10 minutes helps)',
        'Use stairs instead of elevator'
      ],
      whatYouCanDoToday: [
        'Set hourly "stand and stretch" reminders on your phone',
        'Walk for 10 minutes during lunch',
        'Do 10 shoulder rolls every hour'
      ]
    }
  },
  {
    id: 'activity_2',
    title: 'Building an Exercise Habit',
    category: 'activity',
    icon: '💪',
    readTime: '2 min',
    triggers: ['low_activity', 'weight_management'],
    priority: 'medium',
    summary: 'Start small and build consistency before intensity',
    content: {
      overview: 'Most people fail at exercise because they start too big. The secret is starting ridiculously small and building consistency first.',
      keyPoints: [
        'Start with 10 minutes daily (walking counts!)',
        'Same time every day builds habit faster',
        'Track streaks, not intensity',
        'Exercise before making it a "decision" (morning is best)',
        'Celebrate showing up, not performance'
      ],
      whatYouCanDoToday: [
        'Schedule 10 minutes tomorrow morning for a walk',
        'Put exercise clothes out tonight',
        'Tell someone your plan (accountability helps)'
      ]
    }
  },

  // Mental Health / Stress Category
  {
    id: 'stress_1',
    title: 'Quick Stress Relief Techniques',
    category: 'mental_health',
    icon: '🧘',
    readTime: '2 min',
    triggers: ['high_stress', 'anxiety', 'overwhelmed'],
    priority: 'high',
    summary: '5-minute practices that calm your nervous system',
    content: {
      overview: 'Stress triggers your fight-or-flight response. These techniques activate your "rest and digest" system to counter stress immediately.',
      keyPoints: [
        'Box breathing: Inhale 4, hold 4, exhale 4, hold 4 (repeat 5 times)',
        '5-4-3-2-1 grounding: Name 5 things you see, 4 you hear, 3 you feel, 2 you smell, 1 you taste',
        'Progressive muscle relaxation: Tense and release each muscle group',
        'Step outside for 5 minutes (nature reduces cortisol)',
        'Cold water on wrists (cools blood, calms nervous system)'
      ],
      whatYouCanDoToday: [
        'Try box breathing right now for 2 minutes',
        'Save the 5-4-3-2-1 technique in your phone notes',
        'Schedule a 5-minute walk break today'
      ]
    }
  },
  {
    id: 'stress_2',
    title: 'Managing Overwhelm and Burnout',
    category: 'mental_health',
    icon: '🌿',
    readTime: '2 min',
    triggers: ['high_stress', 'burnout', 'low_energy'],
    priority: 'high',
    summary: 'Recognize early signs and take action before it\'s too late',
    content: {
      overview: 'Burnout doesn\'t happen overnight. Recognizing early signs lets you course-correct before it becomes serious.',
      keyPoints: [
        'Early signs: Constant fatigue, irritability, loss of motivation',
        'Set boundaries: Say "no" to protect your energy',
        'Schedule rest like meetings (non-negotiable)',
        'Delegate or eliminate low-value tasks',
        'Seek support early (friends, therapist, mentor)'
      ],
      whatYouCanDoToday: [
        'Identify one task you can say "no" to this week',
        'Block 30 minutes on your calendar for "rest"',
        'Text a friend you trust about how you\'re feeling'
      ]
    }
  },

  // Nutrition Category
  {
    id: 'nutrition_1',
    title: 'Balanced Eating Made Simple',
    category: 'nutrition',
    icon: '🥗',
    readTime: '2 min',
    triggers: ['poor_diet', 'weight_management', 'low_energy'],
    priority: 'medium',
    summary: 'The plate method: No counting, just visual balance',
    content: {
      overview: 'Forget calorie counting. The plate method is a simple visual guide that works for any meal.',
      keyPoints: [
        'Half your plate: Vegetables (colorful, not fried)',
        'Quarter plate: Lean protein (palm-sized portion)',
        'Quarter plate: Complex carbs (brown rice, quinoa, sweet potato)',
        'Add healthy fat: Olive oil, avocado, nuts',
        'Drink water with meals, not sugary drinks'
      ],
      whatYouCanDoToday: [
        'Look at your next meal and adjust proportions',
        'Add one extra vegetable to dinner tonight',
        'Swap white rice/bread for brown rice or quinoa'
      ]
    }
  },
  {
    id: 'nutrition_2',
    title: 'Smart Snacking for Energy',
    category: 'nutrition',
    icon: '🍎',
    readTime: '1 min',
    triggers: ['low_energy', 'poor_diet'],
    priority: 'medium',
    summary: 'Choose snacks that sustain energy instead of crashing it',
    content: {
      overview: 'Most snacks spike blood sugar and cause crashes. Smart snacks combine protein, fiber, and healthy fats for sustained energy.',
      keyPoints: [
        'Apple with almond butter (fiber + protein + fat)',
        'Greek yogurt with berries',
        'Handful of nuts and dark chocolate',
        'Hummus with vegetables',
        'Avoid: Chips, candy, sugary granola bars'
      ],
      whatYouCanDoToday: [
        'Buy one smart snack option at the store today',
        'Prep snack portions for the week',
        'Remove one junk food from your pantry'
      ]
    }
  },

  // Medication Management
  {
    id: 'medication_1',
    title: 'Medication Adherence: Why and How',
    category: 'medication',
    icon: '💊',
    readTime: '2 min',
    triggers: ['missed_medication', 'irregular_medication'],
    priority: 'high',
    summary: 'Simple systems to never miss a dose again',
    content: {
      overview: 'Missing medications reduces their effectiveness and can cause health setbacks. Smart systems make adherence automatic.',
      keyPoints: [
        'Link to existing habit (medication with breakfast)',
        'Use pill organizer (visual confirmation you took it)',
        'Set phone alarms with specific medication names',
        'Keep medications visible (not hidden in cabinets)',
        'Track in app or calendar for accountability'
      ],
      whatYouCanDoToday: [
        'Buy a 7-day pill organizer',
        'Set a daily phone alarm for medication time',
        'Place pills next to your morning coffee or toothbrush'
      ]
    }
  }
];

// Get personalized content recommendations based on user data
export const getPersonalizedHealthContent = (user, recentCheckins = [], medications = []) => {
  try {
    const triggers = identifyUserTriggers(user, recentCheckins, medications);
    
    // Filter content by triggers
    let recommendations = healthContentDatabase.filter(content =>
      content.triggers.some(trigger => triggers.includes(trigger))
    );
    
    // Sort by priority
    recommendations.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
    
    // Return top 3 personalized + 3 general
    const personalized = recommendations.slice(0, 3);
    const general = healthContentDatabase
      .filter(c => !personalized.includes(c))
      .slice(0, 3);
    
    return {
      personalized,
      general,
      allContent: healthContentDatabase
    };
  } catch (error) {
    console.error('Error getting personalized health content:', error);
    return {
      personalized: [],
      general: healthContentDatabase.slice(0, 3),
      allContent: healthContentDatabase
    };
  }
};

// Identify user triggers based on data
const identifyUserTriggers = (user, recentCheckins = [], medications = []) => {
  const triggers = [];
  
  // Analyze check-ins
  if (recentCheckins.length > 0) {
    const avgSleep = calculateAverage(recentCheckins.map(c => c.sleep_hours).filter(Boolean));
    const avgWater = calculateAverage(recentCheckins.map(c => c.water_intake).filter(Boolean));
    const avgEnergy = calculateAverage(recentCheckins.map(c => c.energy_level).filter(Boolean));
    const avgStress = calculateAverage(recentCheckins.map(c => c.stress_level).filter(Boolean));
    
    if (avgSleep && avgSleep < 7) triggers.push('low_sleep_quality', 'difficulty_sleeping');
    if (avgWater && avgWater < 6) triggers.push('low_water_intake');
    if (avgEnergy && avgEnergy < 5) triggers.push('low_energy');
    if (avgStress && avgStress > 6) triggers.push('high_stress', 'anxiety');
    
    // Check for patterns
    const hasLowActivity = recentCheckins.some(c => !c.exercised || c.exercised === false);
    if (hasLowActivity) triggers.push('low_activity', 'sedentary_lifestyle');
  }
  
  // Check health goal
  if (user?.primary_health_goal) {
    const goalTriggers = {
      'better_sleep': ['low_sleep_quality', 'difficulty_sleeping'],
      'weight_management': ['weight_management', 'poor_diet'],
      'reduce_stress': ['high_stress', 'anxiety', 'overwhelmed'],
      'increase_energy': ['low_energy', 'low_activity']
    };
    
    const goalTriggersToAdd = goalTriggers[user.primary_health_goal] || [];
    triggers.push(...goalTriggersToAdd);
  }
  
  // Check medications
  if (medications.length > 0) {
    triggers.push('medication_management');
    
    // Check for missed doses
    const hasMissedDoses = medications.some(m => m.last_taken_at === null);
    if (hasMissedDoses) {
      triggers.push('missed_medication', 'irregular_medication');
    }
  }
  
  return [...new Set(triggers)]; // Remove duplicates
};

// Helper function to calculate average
const calculateAverage = (values) => {
  if (!values || values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
};

// Get content by category
export const getContentByCategory = (category) => {
  return healthContentDatabase.filter(content => content.category === category);
};

// Get single content by ID
export const getContentById = (id) => {
  return healthContentDatabase.find(content => content.id === id);
};

// Category metadata
export const healthCategories = [
  { id: 'sleep', name: 'Sleep', icon: '😴', color: 'indigo' },
  { id: 'nutrition', name: 'Nutrition', icon: '🥗', color: 'green' },
  { id: 'activity', name: 'Activity', icon: '💪', color: 'orange' },
  { id: 'mental_health', name: 'Mental Health', icon: '🧘', color: 'purple' },
  { id: 'medication', name: 'Medication', icon: '💊', color: 'blue' }
];
